import { useEffect, useState } from 'react';
import { api } from './api.js';
import { Login } from './Login.jsx';
import { Incident } from './Incident.jsx';
import { Users } from './Users.jsx';
import { Enquiries } from './Enquiries.jsx';
import { Plans } from './Plans.jsx';
import { SitePlan } from './SitePlan.jsx';
import { NewVersion } from './NewVersion.jsx';
import { NewIncident } from './NewIncident.jsx';
import { ChangePassword } from './ChangePassword.jsx';
import { Wordmark } from './Logo.jsx';
import { ThemeToggle } from './ThemeToggle.jsx';

const ROLE_LABEL = {
  admin: 'Admin', ic: 'Incident Commander', section_lead: 'Section Lead',
  facilitator: 'Facilitator', observer: 'Observer',
};

export default function App() {
  const [session, setSession] = useState(null);
  const [ready, setReady] = useState(false);
  const [incidents, setIncidents] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [link, setLink] = useState('offline');
  const [view, setView] = useState('board');
  const [creating, setCreating] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // On load, ask the server who we are. The cookie is httpOnly so this is the only way
  // to find out — and it means a revoked session lands on the login screen, not on a
  // broken board that looks signed in.
  useEffect(() => {
    api.me().then(setSession).catch(() => setSession(null)).finally(() => setReady(true));
  }, []);

  async function loadIncidents(prefer) {
    try {
      const { incidents } = await api.incidents();
      setIncidents(incidents);
      setOpenId((cur) => prefer ?? cur
        ?? incidents.find((i) => i.status === 'active')?.id ?? incidents[0]?.id ?? null);
    } catch { setIncidents([]); }
  }

  useEffect(() => { if (session) loadIncidents(); }, [session]);

  async function signOut() {
    await api.logout().catch(() => {});
    setSession(null); setIncidents([]); setOpenId(null); setLink('offline'); setView('board');
  }

  if (!ready) return null;
  if (!session) return <Login onSignedIn={setSession} />;

  const may = (p) => session.permissions.includes(p);
  const open = incidents.find((i) => i.id === openId) ?? null;
  // The incident views share a socket, a sub-bar and a log beside them; the admin views
  // have none of those. One flag rather than a growing list of `view !== 'users'` checks,
  // which is how the third one gets forgotten.
  const onIncident = view === 'board' || view === 'actions' || view === 'objectives'
    || view === 'siteplan';

  // Enquiries come from imtlogic.com and belong to IMT Logic, not to a customer. A
  // customer's own admin holds user.manage over their own people and must never see them.
  // Which tenant that is comes from the SERVER — keeping a copy of the name here is how
  // the tab ends up drawn for the wrong tenant, or for nobody, after a rename. The server
  // checks it again on the route, because a tab that is not drawn is not a permission.
  const TABS = [
    { key: 'board', label: 'Board' },
    { key: 'objectives', label: 'Objectives' },
    { key: 'actions', label: 'Actions', badge: Number(open?.actions_late) || null },
    { key: 'siteplan', label: 'Site plan' },
    // Plans are written outside an incident, so the tab is here rather than on the board.
    // plan.read is granted to everybody, including an observer — a section lead ought to be
    // able to read the plan they are about to work to.
    ...(may('plan.read') ? [{ key: 'plans', label: 'Plans' }] : []),
    ...(may('user.manage') ? [{ key: 'users', label: 'People' }] : []),
    ...(may('user.manage') && session.platform ? [{ key: 'enquiries', label: 'Enquiries' }] : []),
  ];

  return (
    <div className="shell">
      <NewVersion />
      <header className="bar">
        <Wordmark size={30} />
        <small>· Incident Command</small>

        <nav className="tabs main">
          {TABS.map((t) => (
            <button key={t.key} className={`tab ${view === t.key ? 'on' : ''}`}
                    onClick={() => setView(t.key)}>
              {t.label}
              {/* Only ever shows when something is actually past its target time. A badge
                  that is always there stops being read within a day. */}
              {t.badge ? <span className="badge">{t.badge}</span> : null}
            </button>
          ))}
        </nav>

        <div className="spacer" />

        {onIncident && (
          <div className="status">
            <span className={`dot ${link}`} />
            {link === 'live' ? 'Live' : 'Reconnecting…'}
          </div>
        )}

        {/* The name is the way in to your own account. Anybody given a first password by an
            administrator needs somewhere to change it, and there was nowhere. */}
        <button className="who" onClick={() => setChangingPassword(true)}
                title="Change your password">
          <b>{session.user.displayName}</b>
          <span className="role">{ROLE_LABEL[session.user.role] || session.user.role}</span>
        </button>
        <ThemeToggle />
        <button className="linkbtn" onClick={signOut}>Sign out</button>
      </header>

      {onIncident && (
        <div className="subbar">
          {incidents.length > 0 && (
            <select value={openId ?? ''} onChange={(e) => { setOpenId(e.target.value); }}>
              {incidents.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.ref} — {i.name}{i.status === 'closed' ? ' (closed)' : ''}
                </option>
              ))}
            </select>
          )}

          {open && (
            <>
              <span className={`pill ${open.mode}`}>{open.mode}</span>
              {open.status === 'closed' && <span className="pill closed">closed</span>}
              {Number(open.p1_open) > 0 && (
                <span className="tally bad">{open.p1_open} P1 open</span>
              )}
              {Number(open.actions_open) > 0 && (
                <span className="tally">{open.actions_open} actions open</span>
              )}
            </>
          )}

          <span className="spacer" />

          {may('incident.update') && open && (
            <button className="hoBtn" onClick={async () => {
              const next = open.status === 'closed' ? 'active' : 'closed';
              await api.updateIncident(open.id, { status: next }).catch(() => {});
              loadIncidents(open.id);
            }}>
              {open.status === 'closed' ? 'Reopen incident' : 'Close incident'}
            </button>
          )}
          {may('incident.create') && (
            <button className="hoBtn" onClick={() => setCreating(true)}>Open new incident</button>
          )}
        </div>
      )}

      <div className={`main ${onIncident ? '' : 'single'}`}>
        {view === 'users' ? <Users session={session} />
          : view === 'plans' ? <Plans session={session} />
          : view === 'enquiries' ? <Enquiries />
          : openId
            ? <Incident incidentId={openId} session={session} view={view}
                        onStatus={setLink} onCounts={() => loadIncidents(openId)} />
            : <div className="card"><div className="empty">
                No incidents yet.{may('incident.create') && ' Open one to get started.'}
              </div></div>}
      </div>

      {changingPassword && <ChangePassword onClose={() => setChangingPassword(false)} />}

      {creating && (
        <NewIncident
          onClose={() => setCreating(false)}
          onCreated={(incident) => {
            setCreating(false);
            setView('board');
            loadIncidents(incident.id);
          }}
        />
      )}
    </div>
  );
}
